import {
  Button,
  Checkbox,
  Drawer,
  FormField,
  Input,
  Select,
  SelectItem,
} from '@ntconsult/aluma-react';
import { useTranslation } from 'react-i18next';
import {
  AXIIS,
  CONVITE,
  ENTREGA,
  ORIGEM_INCLUSAO,
  SIM_NAO,
} from '../hooks/useFiltrosParticipantes';
import type { FiltrosPainel } from '../types/Painel';

const TODOS = 'todos';

type Props = {
  open: boolean;
  onClose: () => void;
  filtros: FiltrosPainel;
  alterar: (parcial: Partial<FiltrosPainel>) => unknown;
  onLimpar: () => void;
};

/** Mobile (< lg): a tabela fica compacta e os filtros por coluna somem; aqui ficam os principais (spec 14 R15). */
export function FiltrosDrawer({ open, onClose, filtros, alterar, onLimpar }: Props) {
  const { t } = useTranslation(['participantes', 'common', 'convites']);

  const texto = (campo: 'matricula' | 'nome' | 'email', rotulo: string) => (
    <FormField label={rotulo}>
      <Input value={filtros[campo]} onChange={(e) => alterar({ [campo]: e.target.value })} />
    </FormField>
  );

  /** Escolha múltipla: valores marcados combinam com OU (spec 24 B). */
  const varios = (
    campo: 'respondeu' | 'brinde' | 'acessou' | 'temEmail' | 'axiis' | 'convite' | 'entrega',
    rotulo: string,
    valores: readonly string[],
    nome: (v: string) => string,
  ) => {
    const marcados: readonly string[] = filtros[campo];
    return (
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-medium text-foreground">{rotulo}</legend>
        {valores.map((v) => (
          <Checkbox
            key={v}
            label={nome(v)}
            checked={marcados.includes(v)}
            onCheckedChange={(c) =>
              alterar({ [campo]: c ? [...marcados, v] : marcados.filter((x) => x !== v) })
            }
          />
        ))}
      </fieldset>
    );
  };

  const opcoes = (
    campo: 'incluidoOrigem',
    rotulo: string,
    valores: readonly string[],
    nome: (v: string) => string,
  ) => (
    <FormField label={rotulo}>
      <Select
        value={filtros[campo] || TODOS}
        onValueChange={(v) =>
          alterar({ [campo]: v === TODOS ? '' : (v as FiltrosPainel[typeof campo]) })
        }
      >
        <SelectItem value={TODOS}>{t('participantes:painel.filtros.todos')}</SelectItem>
        {valores.map((v) => (
          <SelectItem key={v} value={v}>
            {nome(v)}
          </SelectItem>
        ))}
      </Select>
    </FormField>
  );
  const simNao = (v: string) => t(`participantes:painel.filtros.${v}` as never);

  return (
    <Drawer open={open} onClose={onClose} ariaLabel={t('participantes:filtros.avancados')}>
      <Drawer.Header
        title={t('participantes:filtros.avancados')}
        onClose={onClose}
        dismissLabel={t('common:close')}
      />
      <Drawer.Body className="flex flex-col gap-4">
        {texto('matricula', t('participantes:painel.colunas.matricula'))}
        {texto('nome', t('participantes:painel.colunas.nome'))}
        {texto('email', t('participantes:colunas.email'))}
        {varios('temEmail', t('participantes:filtros.temEmail'), SIM_NAO, simNao)}
        {varios('respondeu', t('participantes:painel.colunas.resposta'), SIM_NAO, simNao)}
        {varios('brinde', t('participantes:painel.colunas.brinde'), SIM_NAO, simNao)}
        {varios('convite', t('participantes:painel.colunas.convite'), CONVITE, (v) =>
          t(`convites:status.${v}` as never),
        )}
        {varios('entrega', t('participantes:painel.colunas.endereco'), ENTREGA, (v) =>
          t(`participantes:painel.entrega.${v}` as never),
        )}
        {varios('acessou', t('participantes:filtros.acessou'), SIM_NAO, simNao)}
        {varios('axiis', t('participantes:painel.filtros.axiis'), AXIIS, simNao)}
        {opcoes('incluidoOrigem', t('participantes:colunas.origem'), ORIGEM_INCLUSAO, (v) =>
          t(`participantes:origem.${v}` as never),
        )}
      </Drawer.Body>
      <Drawer.Footer>
        <Button variant="secondary" onClick={onLimpar}>
          {t('participantes:filtros.limparTudo')}
        </Button>
        <Button onClick={onClose}>{t('participantes:filtros.fechar')}</Button>
      </Drawer.Footer>
    </Drawer>
  );
}
